import { useEffect, useState, useCallback } from 'react';
import {
  FileCheck2,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Code,
  X,
  User,
  Clock,
} from 'lucide-react';
import { AuditLogEntry } from '../types';
import api from '../services/api';
import { ExecutiveKpiScorecard } from '../components/ExecutiveKpiScorecard';

const actionBadgeClasses: Record<string, string> = {
  update_triage_status: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  update_notes: 'bg-amber-100 text-amber-800 border-amber-200',
  export_signals: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  create_user: 'bg-purple-100 text-purple-800 border-purple-200',
  update_user_role: 'bg-purple-100 text-purple-800 border-purple-200',
  delete_user: 'bg-rose-100 text-rose-800 border-rose-200',
  login: 'bg-slate-100 text-slate-700 border-slate-200',
  reset_signals_database: 'bg-red-100 text-red-800 border-red-200',
};

const PAGE_SIZE = 25;

export default function AuditPage() {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [actionFilter, setActionFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Selected Log Details Modal
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.audit.list({
        page,
        limit: PAGE_SIZE,
        action: actionFilter || undefined,
        search: searchFilter || undefined,
      });
      setLogs(res.logs);
      setTotal(res.pagination.total);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch audit log trail';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, searchFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const filteredLogs = logs;

  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans text-slate-900 pb-16">
      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 space-y-6">
        {/* SOW Performance & SLA Compliance Dashboard */}
        <ExecutiveKpiScorecard variant="standalone" />

        {/* Page Header */}
        <div className="bg-white rounded-xl p-6 md:p-8 border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.03)] space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-blue-800 bg-blue-50 border border-blue-200 px-2.5 py-0.5 rounded">
                  <FileCheck2 className="w-3 h-3 text-blue-600" />
                  Compliance Trail
                </span>
              </div>
              <h1 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">
                Immutable Regulatory Audit Trail
              </h1>
              <p className="text-xs text-slate-600 max-w-2xl leading-relaxed">
                Complete chronological log of user logins, signal triage modifications, review
                notes, exports, and governance actions for Part 11 verification.
              </p>
            </div>
            <span className="bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 shadow-xs shrink-0">
              <span className="w-2 h-2 rounded-full bg-emerald-600 animate-pulse"></span>
              Tamper-Evident Active
            </span>
          </div>

          {/* Filter Bar */}
          <div className="pt-2 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Filter by user email or resource ID..."
                value={searchFilter}
                onChange={(e) => {
                  setSearchFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full text-xs bg-slate-50/80 border border-slate-200 rounded-lg px-3 py-2.5 pl-9 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:bg-white font-sans transition"
              />
              <Search className="absolute left-3 top-3 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            </div>

            <div className="relative">
              <select
                value={actionFilter}
                onChange={(e) => {
                  setActionFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full text-xs bg-slate-50/80 border border-slate-200 rounded-lg px-3 py-2.5 pl-9 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:bg-white font-medium transition"
              >
                <option value="">All Action Types</option>
                <option value="update_triage_status">Triage Status Updates</option>
                <option value="update_notes">Internal Review Notes</option>
                <option value="export_signals">Signals Exported (CSV/Excel)</option>
                <option value="create_user">Team Member Created</option>
                <option value="update_user_role">User Role Updated</option>
                <option value="delete_user">User Deleted</option>
                <option value="login">User Logins</option>
                <option value="reset_signals_database">Database Reset</option>
              </select>
              <Filter className="absolute left-3 top-3 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="rounded-lg bg-rose-50 p-4 border border-rose-200 text-xs text-rose-950 font-medium shadow-xs">
            {error}
          </div>
        )}

        {/* Table View */}
        <div className="bg-white rounded-xl border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.03)] overflow-hidden">
          {loading ? (
            <div className="p-16 text-center">
              <div className="inline-block w-8 h-8 border-3 border-blue-700 border-t-transparent rounded-full animate-spin mb-3"></div>
              <p className="text-slate-600 text-xs font-semibold">Loading audit log entries...</p>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="p-16 text-center">
              <FileCheck2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <h3 className="font-display text-base font-semibold text-slate-900">
                No audit log records found
              </h3>
              <p className="text-xs text-slate-500 mt-1">Try adjusting the active action filter.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                    <th className="py-3 px-4">Timestamp (UTC)</th>
                    <th className="py-3 px-4">User</th>
                    <th className="py-3 px-4">Action</th>
                    <th className="py-3 px-4">Resource</th>
                    <th className="py-3 px-4 text-right">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                      <td className="py-3.5 px-4 whitespace-nowrap text-slate-600 font-mono text-[11px] tabular-nums">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3 h-3 text-slate-400" />
                          <span>{new Date(log.timestamp).toLocaleString()}</span>
                        </div>
                      </td>
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                          <User className="w-3 h-3 text-slate-400" />
                          <span>{log.user_email || log.user_id}</span>
                        </div>
                        {log.user_role && (
                          <div className="text-[10px] text-blue-600 capitalize font-mono">
                            {log.user_role}
                          </div>
                        )}
                      </td>
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <span
                          className={`px-2.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${
                            actionBadgeClasses[log.action] || 'bg-slate-100 text-slate-800'
                          }`}
                        >
                          {log.action.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 whitespace-nowrap text-slate-700 font-mono text-[11px]">
                        {log.resource_type}: {log.resource_id}
                      </td>
                      <td className="py-3.5 px-4 text-right whitespace-nowrap">
                        <button
                          onClick={() => setSelectedLog(log)}
                          className="bg-slate-50 hover:bg-slate-100 active:scale-[0.98] text-slate-700 hover:text-slate-900 border border-slate-200 px-3 py-1 rounded-lg text-xs font-semibold transition flex items-center gap-1 inline-flex cursor-pointer shadow-xs"
                        >
                          <Code className="w-3 h-3 text-slate-500" />
                          <span>View Payload</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Controls */}
          {total > PAGE_SIZE && (
            <div className="p-4 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between">
              <span className="text-xs text-slate-600 font-medium font-mono tabular-nums">
                Showing {filteredLogs.length} of {total} events
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed shadow-xs transition flex items-center gap-1 cursor-pointer"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Prev
                </button>
                <span className="text-xs text-slate-700 font-mono">Page {page}</span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page * PAGE_SIZE >= total}
                  className="px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed shadow-xs transition flex items-center gap-1 cursor-pointer"
                >
                  Next <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Audit Log JSON Details Modal */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 text-slate-100 rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden">
            <div className="p-4 border-b border-slate-800 flex justify-between items-center bg-slate-950">
              <div className="flex items-center gap-2">
                <span className="bg-blue-500/20 text-blue-400 font-mono text-xs px-2.5 py-1 rounded-md border border-blue-500/30 font-bold">
                  AUDIT EVENT {selectedLog.id}
                </span>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-slate-400 hover:text-white p-1 rounded hover:bg-slate-800 transition cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 flex-1 overflow-y-auto bg-slate-950/80 space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-2 bg-slate-950 p-3 rounded-lg border border-slate-800/80 font-mono text-[11px]">
                <div>
                  User:{' '}
                  <span className="text-blue-400">
                    {selectedLog.user_email || selectedLog.user_id}
                  </span>
                </div>
                <div>
                  Timestamp:{' '}
                  <span className="text-emerald-400">
                    {new Date(selectedLog.timestamp).toISOString()}
                  </span>
                </div>
                <div className="col-span-2">
                  Resource:{' '}
                  <span className="text-amber-400">
                    {selectedLog.resource_type} ({selectedLog.resource_id})
                  </span>
                </div>
              </div>

              <div>
                <div className="font-semibold text-slate-300 mb-1 font-sans text-xs">
                  Event Payload & Audit Evidence:
                </div>
                <pre className="bg-slate-950 p-3.5 rounded-lg border border-slate-800 font-mono text-[11px] text-emerald-400 overflow-x-auto max-h-64 whitespace-pre-wrap select-all">
                  {(() => {
                    if (typeof selectedLog.details !== 'string') {
                      return JSON.stringify(selectedLog.details, null, 2);
                    }
                    try {
                      return JSON.stringify(JSON.parse(selectedLog.details), null, 2);
                    } catch {
                      return selectedLog.details;
                    }
                  })()}
                </pre>
              </div>
            </div>

            <div className="p-4 border-t border-slate-800 bg-slate-950 flex justify-end">
              <button
                onClick={() => setSelectedLog(null)}
                className="bg-blue-700 hover:bg-blue-800 active:scale-[0.98] text-white text-xs font-semibold px-4 py-2 rounded-lg transition cursor-pointer shadow-xs"
              >
                Close Log
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
